import {
  AddressDisplay,
  Badge,
  BlockIndicator,
  Button,
  Input,
  Modal,
  StatusIndicator,
  Tabs,
  Tooltip,
} from "@gotts.ai/ui";
import {
  Menu,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useEffect, useState } from "react";
import { formatUnits } from "viem";

import { useEvmBackend } from "../context";
import { useBlockNumber } from "../hooks/useBlockNumber";
import { useDevenvNavigate } from "../hooks/useDevenvNavigate";

import type { ReactNode } from "react";
import type { Address, Hex } from "viem";

interface AccountBarProps {
  onMenuToggle?: () => void;
  showHamburger?: boolean;
  showCollapseToggle?: boolean;
  sidebarCollapsed?: boolean;
  /** Optional actions rendered in the right section, before the block indicator. */
  actions?: ReactNode;
}

export function AccountBar({
  onMenuToggle,
  showHamburger,
  showCollapseToggle,
  sidebarCollapsed,
  actions,
}: AccountBarProps) {
  const navigate = useDevenvNavigate();
  const {
    address,
    label,
    publicClient,
    chain,
    accounts,
    connectAnvil,
    connectPrivateKey,
    connectInjected,
    disconnect,
  } = useEvmBackend();

  const { data: blockNumber } = useBlockNumber();

  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("anvil");
  const [privateKey, setPrivateKey] = useState("");
  const [pkError, setPkError] = useState("");
  const [injectedError, setInjectedError] = useState("");
  const [balances, setBalances] = useState<Record<string, bigint>>({});

  // Fetch Anvil account balances when modal opens
  useEffect(() => {
    if (!modalOpen) return;
    let cancelled = false;

    async function fetchBalances() {
      const results: Record<string, bigint> = {};
      for (const account of accounts) {
        try {
          results[account.address] = await publicClient.getBalance({
            address: account.address,
          });
        } catch {
          results[account.address] = 0n;
        }
      }
      if (!cancelled) setBalances(results);
    }

    void fetchBalances();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, publicClient]);

  function handleAnvilConnect(addr: Address, lbl: string) {
    connectAnvil(addr, lbl);
    setModalOpen(false);
  }

  function handlePkConnect() {
    setPkError("");
    try {
      connectPrivateKey(privateKey as Hex);
      setModalOpen(false);
      setPrivateKey("");
    } catch (err) {
      setPkError(err instanceof Error ? err.message : "Invalid private key");
    }
  }

  async function handleInjectedConnect() {
    setInjectedError("");
    try {
      await connectInjected();
      setModalOpen(false);
    } catch (err) {
      setInjectedError(
        err instanceof Error ? err.message : "Connection failed",
      );
    }
  }

  // Determine display badge from label
  const isAnvilAccount = accounts.some((a) => a.address === address);
  const chainLabel = chain.name;
  const modeBadge = !address
    ? null
    : isAnvilAccount
      ? chainLabel
      : label === "Private Key"
        ? "PK"
        : label === "Browser Wallet"
          ? "Injected"
          : "Connected";

  const badgeClass = isAnvilAccount
    ? "bg-green-500/20 text-green-400 border border-green-500/30"
    : undefined;

  const toggleBtn = showHamburger ? (
    <button
      onClick={onMenuToggle}
      className="shrink-0 rounded p-1 text-[var(--color-text-dim)] hover:bg-[rgba(255,255,255,0.06)]"
      aria-label="Toggle menu"
    >
      <Menu size={18} />
    </button>
  ) : showCollapseToggle ? (
    <button
      onClick={onMenuToggle}
      className="shrink-0 rounded p-1 text-[var(--color-text-dim)] hover:bg-[rgba(255,255,255,0.06)]"
      aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
    >
      {sidebarCollapsed ? (
        <PanelLeftOpen size={16} />
      ) : (
        <PanelLeftClose size={16} />
      )}
    </button>
  ) : null;

  return (
    <>
      <div className="flex items-center justify-between overflow-hidden border-b border-[var(--glass-border)] bg-[rgba(13,17,23,0.85)] px-2 py-2 [backdrop-filter:var(--glass-blur)] sm:px-4">
        {!address ? (
          <>
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              {toggleBtn}
              <span className="text-text-muted font-mono text-[10px] uppercase tracking-wider">
                No wallet connected
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2 whitespace-nowrap sm:gap-3">
              {actions}
              <Tooltip content={`${chainLabel} \u00b7 Click for TX log`}>
                <button
                  onClick={() => {
                    navigate("txlog");
                  }}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                >
                  <BlockIndicator blockNumber={blockNumber} size="sm" />
                </button>
              </Tooltip>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                Connect
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex min-w-0 shrink items-center gap-2 sm:gap-3">
              {toggleBtn}
              <StatusIndicator status="online" />
              <span className="hidden font-sans text-[13px] font-light text-[var(--color-text)] sm:inline">
                {label}
              </span>
              <AddressDisplay
                address={address}
                leadChars={6}
                tailChars={4}
                fontSize={12}
              />
              {modeBadge && (
                <span className="hidden sm:inline-flex">
                  {badgeClass ? (
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${badgeClass}`}
                    >
                      {modeBadge}
                    </span>
                  ) : (
                    <Badge variant="info">{modeBadge}</Badge>
                  )}
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2 whitespace-nowrap sm:gap-3">
              {actions}
              <Tooltip content={`${chainLabel} \u00b7 Click for TX log`}>
                <button
                  onClick={() => {
                    navigate("txlog");
                  }}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                >
                  <BlockIndicator blockNumber={blockNumber} size="sm" />
                </button>
              </Tooltip>
              {/* Desktop: Switch Account primary, Disconnect as subtle icon */}
              <span className="hidden sm:contents">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setModalOpen(true);
                  }}
                >
                  Switch Account
                </Button>
                <button
                  onClick={disconnect}
                  className="rounded-full p-1.5 text-white/30 transition-colors hover:bg-red-500/10 hover:text-red-400"
                  aria-label="Disconnect"
                  title="Disconnect wallet"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </span>
              {/* Mobile: single ellipsis button opens wallet modal */}
              <button
                onClick={() => {
                  setModalOpen(true);
                }}
                className="rounded p-1 text-[var(--color-text-dim)] hover:bg-[rgba(255,255,255,0.06)] sm:hidden"
                aria-label="Wallet options"
              >
                <MoreVertical size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        size="lg"
        title="Connect Wallet"
      >
        <Tabs
          tabs={[
            { label: "Anvil Accounts", value: "anvil" },
            { label: "Private Key", value: "pk" },
            { label: "Browser Wallet", value: "injected" },
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />

        <div className="mt-4">
          {activeTab === "anvil" && (
            <div className="max-h-80 overflow-y-auto">
              {accounts.map((account, i) => (
                <button
                  key={account.address}
                  onClick={() => {
                    handleAnvilConnect(account.address, account.label);
                  }}
                  className={`flex w-full items-center justify-between px-3 py-2 transition-colors hover:bg-[rgba(201,168,76,0.05)] ${
                    address === account.address
                      ? "border-gold border-l-2 bg-[rgba(201,168,76,0.08)]"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-text-muted font-mono text-[10px]">
                      {i}
                    </span>
                    <span className="text-text-dim font-mono text-[10px]">
                      {account.label}
                    </span>
                    <span className="text-gold font-mono text-[10px]">
                      {account.address.slice(0, 10)}...
                      {account.address.slice(-6)}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-[var(--color-success)]">
                    {(() => {
                      const bal = balances[account.address];
                      return bal !== undefined
                        ? `${parseFloat(formatUnits(bal, 18)).toFixed(1)} ETH`
                        : "...";
                    })()}
                  </span>
                </button>
              ))}
            </div>
          )}

          {activeTab === "pk" && (
            <div className="space-y-3">
              <Input
                type="password"
                placeholder="0x... private key"
                value={privateKey}
                onChange={(e) => {
                  setPrivateKey(e.target.value);
                }}
                hasError={!!pkError}
              />
              {pkError && (
                <p className="text-xs text-[var(--color-error)]">{pkError}</p>
              )}
              <Button
                variant="gold"
                size="sm"
                onClick={handlePkConnect}
                disabled={!privateKey}
                className="w-full"
              >
                Connect
              </Button>
            </div>
          )}

          {activeTab === "injected" && (
            <div className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleInjectedConnect}
                className="w-full"
              >
                Connect Browser Wallet
              </Button>
              {injectedError && (
                <p className="text-xs text-[var(--color-error)]">
                  {injectedError}
                </p>
              )}
              <p className="text-text-muted text-[10px]">
                Make sure MetaMask or another wallet extension is installed and
                pointed at localhost:8545 (chain 31337).
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
